import { DOCUMENT, inject } from '@angular/core';
import { ActivatedRouteSnapshot, ViewTransitionsFeature, withViewTransitions } from '@angular/router';

/** Route data key that marks an app's shell layout route, the parent of every signed-in page. */
export const APP_SHELL_ROUTE_DATA_KEY = 'isAppShell';

/** `sign-in` moves from the auth pages into the app shell, `sign-out` the other way. */
export type SessionTransition = 'sign-in' | 'sign-out';

/**
 * The root attribute the global stylesheet keys the transition's choreography on, set for as long
 * as the transition runs. Spelled as the `dataset` key of `data-lp-session-transition`.
 */
const SESSION_TRANSITION_DATASET_KEY = 'lpSessionTransition';

/**
 * Runs the start and the end of a session as a view transition: every navigation between the auth
 * pages and the app shell, whether through the login form, 2FA, a password reset, the logout or an
 * expired session. Every other navigation skips it and swaps instantly, and browsers without the
 * View Transitions API fall back to the instant swap on their own.
 */
export function withSessionViewTransitions(): ViewTransitionsFeature {
  return withViewTransitions({
    skipInitialTransition: true,
    onViewTransitionCreated: ({ transition, from, to }) => {
      const sessionTransition = resolveSessionTransition(from, to);
      if (!sessionTransition) {
        transition.skipTransition();
        return;
      }

      const rootElement = inject(DOCUMENT).documentElement;
      rootElement.dataset[SESSION_TRANSITION_DATASET_KEY] = sessionTransition;
      void transition.finished.finally(() => delete rootElement.dataset[SESSION_TRANSITION_DATASET_KEY]);
    },
  });
}

/**
 * Whether a navigation starts or ends a session, judged by whether it enters or leaves the app shell.
 *
 * @param {ActivatedRouteSnapshot} from - The root snapshot being left
 * @param {ActivatedRouteSnapshot} to - The root snapshot being entered
 * @returns {SessionTransition | null} The transition, or `null` when both sides share a layout
 */
export function resolveSessionTransition(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): SessionTransition | null {
  const leavesAppShell = rendersAppShell(from);
  const entersAppShell = rendersAppShell(to);

  if (leavesAppShell === entersAppShell) {
    return null;
  }
  return entersAppShell ? 'sign-in' : 'sign-out';
}

function rendersAppShell(root: ActivatedRouteSnapshot): boolean {
  for (let snapshot: ActivatedRouteSnapshot | null = root; snapshot; snapshot = snapshot.firstChild) {
    if (snapshot.routeConfig?.data?.[APP_SHELL_ROUTE_DATA_KEY] === true) {
      return true;
    }
  }
  return false;
}
